import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import { getWord } from 'utils/tenant';
import css from './PreferencesSection.scss';
import emailCss from './AlternateEmail.scss';
import Checkbox from 'SF/Checkbox';
import { isEmptyString, isValidEmail } from 'utils/text';
import Icon from 'SF/MaterialIcon';
import classnames from 'classnames';
import { pressedEnterOrSpace } from 'utils/accessibility';

/**
 * @returns {node} the component for users to configure their alternate email address and corresponding
 * preferences/information. This also checks the relevant permissions and renders the
 * expected custom language.
 **/
const AlternateEmail = ({ 
    allowPreferredEmail, correspondenceTo, isStaff, goToNotificationsTab, mobileEmailInfoLink, preferredEmail,
    setValue, showMobileEmailInfo, showNotificationsLinks
}) => {
    const [isInvalid, setInvalid] = useState(false);
    
    const removeAlternateCorrespondence = () => {
        if (correspondenceTo === 'SCHOOL_MAIL') return;
        if (correspondenceTo === 'BOTH_MAIL') {
            setValue({ correspondenceTo: 'SCHOOL_MAIL' });
        } else if (isStaff) {
            // Only staff can fully opt out of mail
            setValue({ correspondenceTo: '' });
        }
    };

    useEffect(() => {
        if (isEmptyString(preferredEmail)) {
            removeAlternateCorrespondence();
        } else if(!isValidEmail(preferredEmail)) {
            setInvalid(true);
            // if invalid, we won't send notifications to preferred email
            removeAlternateCorrespondence();
            return;
        }
        setInvalid(false);
    }, [preferredEmail]);
    
    const getMobileEmailInfoLink = () => {
        if (!showMobileEmailInfo) return null;
        return (
            <span dangerouslySetInnerHTML={{__html: 
                `<a href="${mobileEmailInfoLink}" class="icon mask-enabled"
                    aria-label="Cell Phone Users: Read more details."
                    aria-haspopup="true">
                    <i class="fa fa-fw fa-mobile-phone">
                        <span class="screenreader">Cell Phone Users: Read more details.</span>
                    </i>
                </a>`
            }} />
        );
    };
    
    const handleOptInChange = (checked) => {
        if (checked) {
            // if a staff user is enabling notifications to preferred email and school email notifications 
            // has already been set, mark as 'BOTH_MAIL'. a student user can never only have 'PREFERRED_MAIL' selected.
            setValue({ correspondenceTo: correspondenceTo === 'SCHOOL_MAIL' || !isStaff ? 'BOTH_MAIL' : 'PREFERRED_MAIL' });
        } else {
            removeAlternateCorrespondence();
        }
    };
    
    if (!allowPreferredEmail) return null;
    return (
        <div id="alternate-email-section">
            <div className={classnames(emailCss['alternate-email'], {[css.error]: isInvalid})}>
                <label id="altEmailPref">{getWord('alternateEmail')} {getMobileEmailInfoLink()}</label>
                <input type="text"
                       aria-labelledby="altEmailPref"
                       onChange={(e) => setValue({ preferredEmail: e.target.value }) }
                       value={preferredEmail} />
                <span className={css['error-message']} aria-hidden={!isInvalid}>
                    <Icon icon="error" aria-hidden="true" />Please provide a valid {getWord('alternateEmail', true)}
                </span>
            </div>
            <div className={classnames(css['profile-adv-check'], {[css.error]: correspondenceTo === ''})}>
                <Checkbox ariaLabel=""
                          aria-labelledby="altEmailNotification"
                          checked={ correspondenceTo === 'BOTH_MAIL' || (isStaff && correspondenceTo === 'PREFERRED_MAIL') }
                          disabled={isEmptyString(preferredEmail) || isInvalid}
                          onChange={handleOptInChange} />
                <span id="altEmailNotification" className={css['profile-adv-options'] + ' ' + css['profile-sub-text']}>
                    Send notifications to my {getWord('alternateEmail', true)} address
                    { isStaff &&
                        <span className={css['error-message']} aria-hidden={correspondenceTo !== ''}>
                            <br/>
                            <Icon icon="error" aria-hidden="true"/>You must have at least one email address selected to receive email notifications
                        </span>
                    }
                    { showNotificationsLinks &&
                        <>
                            <br/>
                            <a href='#'
                               onClick={goToNotificationsTab} 
                               onKeyDown={(e) => { if (pressedEnterOrSpace(e)) goToNotificationsTab()} }>
                                Edit Notification Preferences
                            </a>
                        </>
                    }
                </span>
            </div>
        </div>
    )
};

AlternateEmail.propTypes = {
    allowPreferredEmail: PropTypes.bool,
    correspondenceTo: PropTypes.oneOf(['BOTH_MAIL', 'SCHOOL_MAIL', 'PREFERRED_MAIL', '']),
    goToNotificationsTab: PropTypes.func,
    isStaff: PropTypes.bool,
    mobileEmailInfoLink: PropTypes.string,
    preferredEmail: PropTypes.string,
    setValue: PropTypes.func.isRequired,
    showMobileEmailInfo: PropTypes.bool,
    showNotificationsLinks: PropTypes.bool
};

export default AlternateEmail;
